import type { LegalDocPair } from '../types'

/**
 * Términos de servicio: el acuerdo principal. Los demás documentos (uso
 * aceptable, facturación, ventas, dominios, IA, beta, privacidad…) cuelgan de
 * éste y lo detallan; aquí se dice cuáles aplican y cuál manda si chocan.
 *
 * Estructura inspirada en la de los constructores de sitios de referencia
 * —cuenta, contenido, responsabilidades, terceros, propiedad, derechos,
 * facturación, privacidad, propiedad intelectual, confidencialidad, garantías,
 * indemnización, terminación, cambios, disputas, sanciones, generalidades—,
 * escrita de cero y adaptada a lo que véltica hace de verdad.
 */
export const TERMS: LegalDocPair = {
  en: {
    title: 'Terms of Service',
    summary: 'The agreement that governs your use of véltica.',
    intro: [
      'These Terms of Service (the “Terms”) are a binding agreement between you and {operator}, doing business as véltica (“véltica”, “we”, “us” or “our”). They govern your access to and use of veltica.com, app.veltica.com, the editor, the sites we host, our interfaces and every related product and service (together, the “Platform”).',
      'By creating an account, clicking to accept, or using the Platform, you agree to these Terms. If you do not agree, do not use the Platform.',
    ],
    sections: [
      {
        title: 'Welcome to véltica',
        subs: [
          {
            title: 'What véltica is',
            blocks: [
              'véltica lets you design, manage and publish websites and, optionally, sell products and services, take bookings, collect form submissions and run a blog from them. We host published sites on our infrastructure, at an address we assign or on a domain you connect.',
            ],
          },
          {
            title: 'Documents that apply',
            blocks: [
              'Your use of the Platform is also governed by the following documents, which are part of this agreement (together with these Terms, the “Agreement”): the [Acceptable Use Policy](doc:aup), the [Copyright Policy](doc:copyright), the [Billing and Refund Policy](doc:billing), the [Privacy Policy](doc:privacy) and the [Cookie Policy](doc:cookies). Where they apply to what you do, so do the [Seller Terms](doc:sellers), the [Domain Terms](doc:domains), the [AI Terms](doc:ai), the [Beta Program Terms](doc:beta) and the [Data Processing Addendum](doc:dpa).',
              'If one of those documents covers a specific subject and conflicts with these Terms on that subject, the specific document prevails for that subject. Otherwise these Terms prevail.',
            ],
          },
          {
            title: 'Who is who',
            blocks: [
              '“You” means the person or organization that creates an account. If you use the Platform for an organization, you confirm that you have authority to bind it, and “you” includes it.',
              'Some words have a specific meaning in the Agreement. An “Account” is your registration on the Platform. A “Site” is a website or project you create or publish with the Platform. “Customer Content” is everything you upload, create, enter or publish through the Platform, including text, images, code, product and service listings, prices, form and booking settings, and any personal data you put in it. “End Users” are the people who visit or interact with your Sites, including buyers and people who send a form or book. “Fees” are amounts you owe us for paid plans, domains or other paid services.',
            ],
          },
        ],
      },
      {
        title: 'Your Account',
        subs: [
          {
            title: 'Eligibility',
            blocks: [
              'You must be at least 18 years old, or the age of majority where you live if that is higher, and able to enter into a binding contract. You must not be barred from using the Platform under the laws that apply to you. If you sign up for an organization, you must have authority to accept these Terms on its behalf. If we have previously suspended or terminated your Account, you need our permission before opening a new one.',
            ],
          },
          {
            title: 'Signing up',
            blocks: [
              'You must give accurate and current information when you register and keep it up to date. An Account belongs to one person or organization; do not share your credentials. We send important notices to your account email address, so keep access to it.',
            ],
          },
          {
            title: 'Your responsibility for your Account',
            blocks: [
              'You are responsible for everything that happens under your Account, including actions by team members and anyone else you give access to. Keep your credentials secure and tell us right away at {email} if you suspect unauthorized use. We are not liable for losses caused by unauthorized use of your Account unless they result from our own breach of the Agreement.',
            ],
          },
          {
            title: 'Team members, agencies and transfers',
            blocks: [
              'You may invite other people to a project with the roles the Platform offers. The owner of the project is responsible for what its members do, and the owner’s plan sets the limits and the fees that apply to the project. You may hand a Site over to another Account with the transfer feature; from then on the receiving Account is responsible for it. We cannot settle disputes between you and an agency or collaborator, but we may follow the instructions of the verified owner of the Account.',
            ],
          },
          {
            title: 'Account ownership disputes',
            blocks: [
              'If two or more parties claim the same Account or Site, we may ask for proof of ownership, freeze changes while we review it, or follow a court order. We do not decide the underlying dispute and we are not liable for acting in good faith.',
            ],
          },
          {
            title: 'Deleting your Account',
            blocks: [
              'You can delete your Account from the settings. Deleting projects and accounts follows the retention periods in the [Privacy Policy](doc:privacy), including the 30 days during which a deleted project can be restored. Domains you registered through us remain yours until they expire. Deleting your Account does not cancel Fees already due.',
            ],
          },
        ],
      },
      {
        title: 'Your Content',
        subs: [
          {
            title: 'Your content stays yours',
            blocks: ['You own your Customer Content. We do not claim ownership of it.'],
          },
          {
            title: 'The license you give us',
            blocks: [
              'You grant us a worldwide, non-exclusive, royalty-free license, which we may sublicense only to the service providers that help us run the Platform, to host, store, copy, process, transmit, adapt (for example to resize images, create thumbnails and screenshots for your dashboard, or format code), display and publish your Customer Content, solely to provide, secure, support and improve the Platform and to publish your Sites as you direct.',
              'The license continues for backup copies until they are overwritten in their ordinary cycles and for as long as we need to keep information to meet a legal obligation. We do not use your Customer Content to advertise véltica and we do not train AI models on it.',
            ],
          },
          {
            title: 'Featuring your Site',
            blocks: [
              'We will not publicly feature your Site, your brand or your Customer Content in our marketing, galleries or showcases without your permission. Sites on plans that include the véltica badge display it, as described in section 6.5.',
            ],
          },
          {
            title: 'Back up your content',
            blocks: [
              'We make backups available according to your plan and we take reasonable care of your data, but the Platform is not a guaranteed archive. Keep your own copies of anything important. Our liability for loss of data is subject to section 12.',
            ],
          },
          {
            title: 'Removing content',
            blocks: [
              'You can remove Customer Content or delete a project at any time. Removing a Site unpublishes it immediately, and a deleted project can be restored for 30 days. Copies held by caches, search engines and other third parties are outside our control and may persist for some time.',
            ],
          },
          {
            title: 'Sensitive information you must not collect',
            blocks: [
              'Do not use forms, bookings or any other part of the Platform to collect payment card numbers, card security codes, bank credentials, passwords, government identification numbers or protected health information, unless we have agreed otherwise in writing. Card payments must go through Stripe; véltica does not receive or store card data from your End Users. véltica is not designed to be used for information regulated by HIPAA and we do not sign business associate agreements.',
            ],
          },
          {
            title: 'Removal by us and child safety',
            blocks: [
              'We may remove or disable Customer Content that we reasonably believe violates the Agreement or the law. We have zero tolerance for child sexual abuse material. If we become aware of it, we will remove it, preserve the evidence, and report it to the National Center for Missing & Exploited Children or the competent authorities as the law requires. We may use automated tools to detect it.',
            ],
          },
        ],
      },
      {
        title: 'Your Responsibilities',
        subs: [
          {
            title: 'Rights to your content',
            blocks: [
              'You must own, or have all the licenses and permissions needed for, everything you put in a Site, including text, images, fonts, music, video, code, trademarks and personal data. Stock images and other third-party media you add remain subject to their own licenses.',
            ],
          },
          {
            title: 'Compliance with laws',
            blocks: [
              'You must comply with all laws that apply to your Sites and your business, including those on consumer protection, advertising, privacy and data protection, cookies and consent, email and marketing communications, accessibility, taxes, licensing and export. You are solely responsible for the privacy policy, terms and notices on your Sites. The policy templates we offer are provided for convenience only; they are not legal advice and do not make your Site compliant by themselves.',
            ],
          },
          {
            title: 'Acceptable Use Policy',
            blocks: ['You must follow the [Acceptable Use Policy](doc:aup), which forms part of the Agreement.'],
          },
          {
            title: 'Your End Users',
            blocks: [
              'You are solely responsible for your relationship with your End Users. You decide what personal data your Sites collect and why, you make the disclosures the law requires, and you answer their requests. For End User data, you are the controller (or business) and we act as your processor (or service provider) under the [Data Processing Addendum](doc:dpa). véltica is not a party to your dealings with End Users and is not responsible for the content, products or services of your Sites.',
            ],
          },
          {
            title: 'Using the Platform',
            blocks: [
              'You may not, and may not help anyone to:',
              {
                list: [
                  'copy, modify, reverse engineer or decompile the Platform, except where the law allows it despite this restriction;',
                  'scrape or crawl the Platform, or use bots or automation to create accounts, generate content at scale or get around limits;',
                  'resell or sublicense the Platform, other than building and managing Sites for your clients through your own Account;',
                  'probe, scan or test the vulnerability of the Platform, or interfere with it or with other users, without our written permission;',
                  'circumvent plan limits, security or technical measures;',
                  'misrepresent your identity or your relationship with véltica; or',
                  'use the Platform to build a competing service by copying it.',
                ],
              },
            ],
          },
          {
            title: 'Reasonable use of resources',
            blocks: [
              'Our plans do not cap the number of visits to your Sites. In exchange, the Platform is meant to host and publish websites, not to distribute files at scale, serve as a download repository, host video or audio for playback from other sites, store backups or unrelated files, or act as a content delivery network for third parties. If an Account’s consumption is disproportionate to its plan, we will contact you to find a solution before limiting the service.',
            ],
          },
          {
            title: 'Reporting security issues',
            blocks: [
              'If you find a vulnerability, tell us at {email} and give us reasonable time to fix it before disclosing it. Do not access data that is not yours, degrade the Platform or expose End Users while you investigate.',
            ],
          },
        ],
      },
      {
        title: 'Third-party services and product-specific terms',
        subs: [
          {
            title: 'Product-specific terms',
            blocks: [
              'Some features have their own terms, which are part of the Agreement when you use them: the [Seller Terms](doc:sellers) for selling and taking payments, the [Domain Terms](doc:domains) for domains, the [AI Terms](doc:ai) for AI features and the [Beta Program Terms](doc:beta) for beta features.',
            ],
          },
          {
            title: 'Third-party services and integrations',
            blocks: [
              'Parts of the Platform depend on third parties, for example Stripe for payments, Google for sign-in, calendars and AI, our domain registrar, email delivery providers, and optional integrations you enable. Their terms and privacy practices apply to your use of them, and you may need to accept them separately. We do not control those services, we do not guarantee their availability, and they may change or stop at any time. When you connect one, you authorize us to exchange the information needed for it to work.',
            ],
          },
          {
            title: 'Content of others; links; custom code',
            blocks: [
              'Sites are created by our users. We do not endorse, verify or control them and we are not responsible for their content or for what they sell. Sites may link to third-party websites we do not control. If you add custom code or embed third-party scripts to your Site, that code runs on your Site under your responsibility, including its security, its behavior and the consents it requires.',
            ],
          },
          {
            title: 'Templates and stock media',
            blocks: [
              'Templates, blocks and designs we provide are licensed to you only for use in Sites built on the Platform. You may not redistribute or resell them as templates. Stock images and other media obtained through the Platform are subject to the license of their source.',
            ],
          },
        ],
      },
      {
        title: 'What we own',
        subs: [
          {
            title: 'véltica’s intellectual property',
            blocks: [
              'véltica owns the Platform and everything that makes it up, including its software, designs, template and block libraries, documentation, name, logos and every improvement to them, other than your Customer Content. The Platform is licensed to you, not sold.',
            ],
          },
          {
            title: 'License to use the Platform',
            blocks: [
              'While you comply with the Agreement, we grant you a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to use the Platform for its intended purpose.',
            ],
          },
          {
            title: 'Feedback',
            blocks: [
              'If you give us ideas, suggestions or feedback, we may use them freely, without obligation or compensation to you, and without having to credit you.',
            ],
          },
          {
            title: 'Beta features',
            blocks: [
              'Features we label as beta, preview or “coming soon” are provided under the [Beta Program Terms](doc:beta).',
            ],
          },
          {
            title: 'Our name and the véltica badge',
            blocks: [
              'Do not use the véltica name, logo or marks except to truthfully refer to the Platform. Sites on plans that include the véltica badge must keep it; do not hide, obscure or remove it by technical means unless your plan allows removing it.',
            ],
          },
        ],
      },
      {
        title: 'Our rights',
        subs: [
          {
            title: 'Important things we can do',
            blocks: [
              'We reserve the following rights, which we may exercise at any time at our discretion and, where the law allows, without liability to you: (a) change any part of the Platform or its functionality; (b) suspend or discontinue any part of the Platform; (c) limit, suspend or end your access to any part of the Platform; (d) limit, disable or remove access to your Account, your Sites or your Customer Content; (e) change our eligibility criteria; (f) monitor or scan your Account and Customer Content for abuse, spam, fraud, malware or other violations, using automated tools where appropriate, and act on what we find; and (g) adjust plans, limits and prices as described in section 15.',
            ],
          },
          {
            title: 'How we usually handle violations',
            blocks: [
              'Where it is reasonable and safe to do so, we will ask you to fix a problem before we act. Serious or repeated violations, risks to people or to the Platform, and legal obligations may lead us to act immediately, including by suspending or terminating your Account. You can write to {email} to appeal; we are not obliged to reinstate content or accounts.',
            ],
          },
          {
            title: 'Legal requests',
            blocks: [
              'We may disclose information when we believe it is required by law or valid legal process, or to protect the rights, safety or property of people or of the Platform. Where the law allows and it is reasonable, we will tell you.',
            ],
          },
        ],
      },
      {
        title: 'Billing, payments and renewals',
        subs: [
          {
            title: 'Fees and payment',
            blocks: [
              'Paid plans are billed in advance, monthly or yearly, through our payment processor, Stripe. You authorize us to charge your payment method for the plan you choose and for anything else you buy from us, such as a domain. Fees are stated in US dollars unless the Platform shows another currency at checkout.',
            ],
          },
          {
            title: 'Renewal and cancellation',
            blocks: [
              'Paid plans renew automatically for the same period until you cancel. You can cancel at any time from Billing; cancelling stops the next renewal, you keep your plan until the end of the period you already paid for, and then your Account moves to the free plan. Nothing is deleted when a plan ends, but you may be unable to add more than the free plan allows.',
            ],
          },
          {
            title: 'Refunds',
            blocks: [
              'Fees already charged are not refundable, and neither is the unused part of a period, except where the law requires it, where we were unable to provide the service because of a fault of ours, or where you were charged by mistake and write to us within 30 days of the charge. See the [Billing and Refund Policy](doc:billing).',
            ],
          },
          {
            title: 'Taxes',
            blocks: [
              'Fees do not include taxes, duties or similar charges, which you are responsible for unless we tell you at checkout that we collect them. If we are required to collect a tax, we will add it to the charge.',
            ],
          },
          {
            title: 'Failed payments',
            blocks: [
              'If a payment fails, we or our processor may retry it. If it cannot be collected, your paid features may be suspended and your Account may return to the free plan at the end of the period, without deleting your content. Disputing a charge with your bank instead of contacting us first may lead us to suspend the Account and to lock the transfer of any domain we granted with the plan.',
            ],
          },
          {
            title: 'Sales made through your Sites and other charges',
            blocks: [
              'The fees we keep on sales you make through your Sites are set out in the [Seller Terms](doc:sellers). Domain charges are covered by the [Domain Terms](doc:domains).',
            ],
          },
        ],
      },
      {
        title: 'Privacy and security',
        blocks: [
          'Our [Privacy Policy](doc:privacy) explains how we handle personal data about you. For personal data of your End Users that we process on your behalf, the [Data Processing Addendum](doc:dpa) applies, and our providers are listed on the [Subprocessors](doc:subprocessors) page.',
          'We use reasonable technical and organizational measures to protect the Platform and your data, but no system is perfectly secure and we cannot guarantee absolute security. If we become aware of a breach of security that affects your personal data, we will tell you without undue delay, as the law requires. You are responsible for keeping your credentials secure and for the security of any code or integrations you add to your Sites.',
        ],
      },
      {
        title: 'Copyright and intellectual property',
        blocks: [
          'We respect intellectual property and expect you to do the same. If you believe content on a Site infringes your copyright, follow the process in our [Copyright Policy](doc:copyright). We may remove content and terminate the accounts of users who repeatedly infringe.',
        ],
      },
      {
        title: 'Confidentiality',
        subs: [
          {
            title: 'Confidential information',
            blocks: [
              '“Confidential Information” means non-public information that one party discloses to the other in connection with the Agreement and that is marked confidential or that a reasonable person would understand to be confidential, such as unreleased features, security details and non-public pricing. It does not include information that is or becomes public through no fault of the recipient, that the recipient already knew or received lawfully from someone else, or that it develops independently. Customer Content that you publish on a public Site is not confidential.',
            ],
          },
          {
            title: 'Obligations',
            blocks: [
              'The recipient will use Confidential Information only to exercise its rights and perform its obligations under the Agreement, will protect it with at least reasonable care, and will not disclose it except to people who need to know it and are bound to keep it confidential, or where the law requires, in which case it will notify the other party where lawful. These obligations continue for three years after the Agreement ends.',
            ],
          },
        ],
      },
      {
        title: 'Disclaimers and limitation of liability',
        subs: [
          {
            title: 'Disclaimer of warranties',
            blocks: [
              'THE PLATFORM IS PROVIDED “AS IS” AND “AS AVAILABLE”. TO THE MAXIMUM EXTENT PERMITTED BY LAW, VÉLTICA DISCLAIMS ALL WARRANTIES, EXPRESS, IMPLIED OR STATUTORY, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE OR SECURE, OR THAT DEFECTS WILL BE CORRECTED. WE DO NOT PROMISE ANY RESULT, INCLUDING TRAFFIC, SEARCH ENGINE RANKING, SALES OR REVENUE. THIRD-PARTY SERVICES, AI-GENERATED OUTPUT AND BETA FEATURES ARE PROVIDED WITHOUT WARRANTY OF ANY KIND. NOTHING WE PROVIDE IS LEGAL, TAX OR FINANCIAL ADVICE.',
            ],
          },
          {
            title: 'Limitation of liability',
            blocks: [
              'TO THE MAXIMUM EXTENT PERMITTED BY LAW, VÉLTICA AND {operator}, AND OUR CONTRACTORS AND PROVIDERS, WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL OR BUSINESS OPPORTUNITY, ARISING OUT OF OR RELATED TO THE AGREEMENT OR THE PLATFORM, WHETHER IN CONTRACT, TORT (INCLUDING NEGLIGENCE) OR OTHERWISE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF THOSE DAMAGES.',
              'TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THE AGREEMENT OR THE PLATFORM WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE TWELVE MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM AND (B) US$100.',
              'Some places do not allow certain exclusions or limits, so parts of this section may not apply to you. Nothing in the Agreement limits liability that cannot be limited by law, including for fraud, gross negligence or willful misconduct. These limits are a basic part of the agreement between us and apply even if a remedy fails of its essential purpose.',
            ],
          },
        ],
      },
      {
        title: 'Indemnification',
        blocks: [
          'You will defend, indemnify and hold harmless véltica, {operator}, and our contractors and providers from and against any claim, loss, liability, damage and cost, including reasonable attorneys’ fees, arising out of or related to: (a) your Customer Content and your Sites; (b) the products and services you offer and your sales, refunds, disputes, taxes and dealings with End Users; (c) your violation of the Agreement or of any law; or (d) your infringement or violation of any third party’s rights.',
          'We will notify you of the claim promptly and cooperate reasonably at your expense. You may not settle a claim in a way that imposes obligations on us or admits fault on our behalf without our written consent.',
        ],
      },
      {
        title: 'Changes to the Platform and termination',
        subs: [
          {
            title: 'Changes to the Platform',
            blocks: [
              'The Platform evolves. We may add, change or remove features. We will try to give notice of changes that significantly reduce what a paid plan includes, where that is reasonably possible.',
            ],
          },
          {
            title: 'Termination by you',
            blocks: [
              'You can stop using the Platform and delete your Account at any time. Cancelling a paid plan is separate and is explained in section 8.',
            ],
          },
          {
            title: 'Suspension or termination by us',
            blocks: [
              'We may suspend or terminate your access if you breach the Agreement, if your use poses a security or legal risk, if we are required by law, or if fees remain unpaid. We may also end the Agreement without cause by giving you 30 days’ notice; in that case we will refund the prepaid Fees for the period after termination takes effect. We do not refund Fees when we terminate or suspend for your breach.',
            ],
          },
          {
            title: 'Effects of termination',
            blocks: [
              'When your access ends, your Sites are unpublished and your right to use the Platform stops. We handle your data according to the retention periods in the [Privacy Policy](doc:privacy), including the period during which a deleted project can be restored, except where we terminated for a serious violation or the law requires otherwise. Save what you need before you leave. Fees already due remain payable.',
            ],
          },
          {
            title: 'What survives',
            blocks: [
              'Sections that by their nature should continue will survive termination, including those on Customer Content licenses needed for backups, ownership, fees owed, confidentiality, disclaimers, limitation of liability, indemnification, disputes and the general provisions.',
            ],
          },
        ],
      },
      {
        title: 'Changes to these Terms',
        blocks: [
          'We may amend the Agreement from time to time. For material changes, we will notify you by email or inside the Platform at least 30 days before they take effect, except where a shorter period is needed for legal or security reasons or because we are adding a new feature. The date and version at the top show what is in force. If you keep using the Platform after a change takes effect, you accept it. If you do not agree, cancel before it takes effect. You can ask us at {email} for earlier versions.',
          'Promotional prices, such as those under the Beta program, are honored for the period stated when you purchased them.',
        ],
      },
      {
        title: 'Dispute resolution',
        subs: [
          {
            title: 'Informal negotiation first',
            blocks: [
              'Before starting any formal proceeding, you agree to write to us at {email} describing the dispute and what you want, and we will both try in good faith to resolve it for at least 30 days.',
            ],
          },
          {
            title: 'Governing law',
            blocks: [
              'The Agreement and any dispute arising out of it are governed by the laws of the State of California and applicable United States federal law, without regard to conflict of laws principles. The United Nations Convention on Contracts for the International Sale of Goods does not apply.',
            ],
          },
          {
            title: 'Courts',
            blocks: [
              'If a dispute is not resolved informally, it will be brought exclusively in the state or federal courts located in {county} County, California, and you and we consent to their personal jurisdiction and venue.',
            ],
          },
          {
            title: 'Exceptions',
            blocks: [
              'Either party may seek an injunction or other urgent relief in any competent court to protect intellectual property or confidential information, and either party may bring an individual claim in small claims court where it qualifies.',
            ],
          },
          {
            title: 'Time limit',
            blocks: [
              'To the extent the law allows, any claim arising out of the Agreement must be brought within one year after it arose, or it is permanently barred.',
            ],
          },
          {
            title: 'Consumers outside the United States',
            blocks: [
              'If you use the Platform as a consumer, the mandatory consumer protection laws of the country where you live still apply to you, and where the law requires it you may bring claims in the courts of your place of residence.',
            ],
          },
          {
            title: 'Notice to California users',
            blocks: [
              'Under California Civil Code section 1789.3, California users are entitled to the following notice: the Platform is provided by {operator}; charges are described in section 8; to resolve a complaint or get more information, write to {email}; and you may contact the Complaint Assistance Unit of the Division of Consumer Services of the California Department of Consumer Affairs in writing at 1625 North Market Blvd., Suite N 112, Sacramento, California 95834, or by telephone at (916) 445-1254 or (800) 952-5210.',
            ],
          },
        ],
      },
      {
        title: 'Economic sanctions and export controls',
        blocks: [
          'You confirm that you are not located in, and are not ordinarily resident in, a country or territory subject to comprehensive United States sanctions, that you are not on any United States list of restricted or prohibited parties, and that you will not use the Platform in violation of United States export controls or sanctions or those of any other applicable jurisdiction. We may restrict or end access to comply with those laws.',
        ],
      },
      {
        title: 'General',
        subs: [
          {
            title: 'Assignment',
            blocks: [
              'You may not assign or transfer the Agreement, or any right under it, without our written consent. We may assign it in connection with a merger, acquisition, reorganization or sale of assets, or to an affiliate. Any assignment that breaks this rule is void.',
            ],
          },
          {
            title: 'Entire agreement and severability',
            blocks: [
              'The Agreement is the entire agreement between you and us about the Platform and replaces earlier agreements on the same subject. If a provision is found unenforceable, it will be enforced to the maximum extent possible and the rest of the Agreement stays in effect.',
            ],
          },
          {
            title: 'No waiver',
            blocks: ['If we do not enforce a right at some moment, we do not give it up. A waiver is valid only if it is in writing and signed by us.'],
          },
          {
            title: 'Events beyond our control',
            blocks: [
              'We are not liable for a failure or delay caused by events beyond our reasonable control, including outages of internet, hosting or payment providers, attacks, natural disasters, war, government action, labor disputes or pandemics.',
            ],
          },
          {
            title: 'Notices and electronic communications',
            blocks: [
              'You agree to receive notices from us electronically, by email to your account address or inside the Platform, and that they satisfy any legal requirement that a notice be in writing. Notices to us go to {email}.',
            ],
          },
          {
            title: 'No third-party beneficiaries; relationship',
            blocks: [
              'The Agreement does not create third-party beneficiaries, and nothing in it creates a partnership, joint venture, employment or agency relationship between you and us.',
            ],
          },
          {
            title: 'Interpretation',
            blocks: ['“Including” means “including without limitation”. Headings are for convenience and do not affect interpretation.'],
          },
          {
            title: 'Languages',
            blocks: [
              'We publish the Agreement in English and in Spanish. The translation is for convenience; if there is a discrepancy, the English version prevails to the extent the law allows.',
            ],
          },
          {
            title: 'Contact',
            blocks: ['Questions about these Terms: {email}.'],
          },
        ],
      },
    ],
  },

  es: {
    title: 'Términos de servicio',
    summary: 'El acuerdo que rige el uso de véltica.',
    intro: [
      'Estos Términos de servicio (los «Términos») son un acuerdo vinculante entre tú y {operator}, que opera bajo el nombre véltica («véltica», «nosotros» o «nuestro»). Regulan tu acceso y uso de veltica.com, app.veltica.com, el editor, los sitios que alojamos, nuestras interfaces y todos los productos y servicios relacionados (en conjunto, la «Plataforma»).',
      'Al crear una cuenta, aceptar con un clic o usar la Plataforma, aceptas estos Términos. Si no estás de acuerdo, no uses la Plataforma.',
    ],
    sections: [
      {
        title: 'Te damos la bienvenida a véltica',
        subs: [
          {
            title: 'Qué es véltica',
            blocks: [
              'véltica te permite diseñar, administrar y publicar sitios web y, si quieres, vender productos y servicios, tomar reservas, recibir formularios y llevar un blog desde ellos. Alojamos los sitios publicados en nuestra infraestructura, en una dirección que asignamos o en un dominio que conectes.',
            ],
          },
          {
            title: 'Documentos que aplican',
            blocks: [
              'El uso de la Plataforma se rige también por los siguientes documentos, que forman parte de este acuerdo (junto con estos Términos, el «Acuerdo»): la [Política de uso aceptable](doc:aup), la [Política de derechos de autor](doc:copyright), la [Política de facturación y reembolsos](doc:billing), la [Política de privacidad](doc:privacy) y la [Política de cookies](doc:cookies). Cuando se aplican a lo que haces, también aplican los [Términos de ventas y pagos](doc:sellers), los [Términos de dominios](doc:domains), los [Términos de inteligencia artificial](doc:ai), los [Términos del programa Beta](doc:beta) y el [Acuerdo de procesamiento de datos](doc:dpa).',
              'Si uno de esos documentos trata un tema concreto y choca con estos Términos en ese tema, prevalece el documento específico para ese tema. En lo demás prevalecen estos Términos.',
            ],
          },
          {
            title: 'Quién es quién',
            blocks: [
              '«Tú» es la persona u organización que crea una cuenta. Si usas la Plataforma para una organización, confirmas que tienes autoridad para obligarla, y «tú» la incluye.',
              'Algunas palabras tienen un sentido concreto en el Acuerdo. Una «Cuenta» es tu registro en la Plataforma. Un «Sitio» es un sitio web o proyecto que creas o publicas con la Plataforma. El «Contenido del cliente» es todo lo que subes, creas, escribes o publicas en la Plataforma, incluidos textos, imágenes, código, fichas de productos y servicios, precios, ajustes de formularios y reservas, y cualquier dato personal que incluyas. Los «Usuarios finales» son las personas que visitan o interactúan con tus Sitios, incluidos compradores y quienes envían un formulario o reservan. Las «Tarifas» son los importes que nos debes por planes de pago, dominios u otros servicios de pago.',
            ],
          },
        ],
      },
      {
        title: 'Tu cuenta',
        subs: [
          {
            title: 'Requisitos',
            blocks: [
              'Debes tener al menos 18 años, o la mayoría de edad de donde vivas si es mayor, y poder celebrar un contrato vinculante. No debes tener prohibido usar la Plataforma según las leyes que te apliquen. Si te registras para una organización, debes tener autoridad para aceptar estos Términos en su nombre. Si antes suspendimos o cerramos tu Cuenta, necesitas nuestro permiso para abrir otra.',
            ],
          },
          {
            title: 'Registro',
            blocks: [
              'Debes dar información exacta y actual al registrarte y mantenerla al día. Una Cuenta pertenece a una persona u organización; no compartas tus credenciales. Enviamos los avisos importantes al correo de tu cuenta, así que conserva el acceso a él.',
            ],
          },
          {
            title: 'Tu responsabilidad sobre tu Cuenta',
            blocks: [
              'Eres responsable de todo lo que ocurra en tu Cuenta, incluidas las acciones de miembros de tu equipo y de cualquiera a quien des acceso. Cuida tus credenciales y avísanos de inmediato en {email} si sospechas un uso no autorizado. No respondemos por pérdidas causadas por el uso no autorizado de tu Cuenta, salvo que provengan de un incumplimiento nuestro del Acuerdo.',
            ],
          },
          {
            title: 'Equipos, agencias y transferencias',
            blocks: [
              'Puedes invitar a otras personas a un proyecto con los roles que ofrece la Plataforma. El propietario del proyecto responde de lo que hagan sus miembros, y su plan fija los límites y las tarifas que aplican al proyecto. Puedes entregar un Sitio a otra Cuenta con la función de transferencia; desde ese momento, la Cuenta que lo recibe es responsable de él. No podemos resolver disputas entre tú y una agencia o colaborador, pero podemos seguir las instrucciones del propietario verificado de la Cuenta.',
            ],
          },
          {
            title: 'Disputas sobre la titularidad de una Cuenta',
            blocks: [
              'Si dos o más partes reclaman la misma Cuenta o Sitio, podemos pedir pruebas de titularidad, congelar cambios mientras las revisamos o seguir una orden judicial. No resolvemos la disputa de fondo y no respondemos por actuar de buena fe.',
            ],
          },
          {
            title: 'Eliminar tu Cuenta',
            blocks: [
              'Puedes eliminar tu Cuenta desde los ajustes. La eliminación de proyectos y cuentas sigue los plazos de conservación de la [Política de privacidad](doc:privacy), incluidos los 30 días durante los cuales un proyecto eliminado se puede restaurar. Los dominios que registraste con nosotros siguen siendo tuyos hasta que venzan. Eliminar tu Cuenta no cancela las Tarifas ya devengadas.',
            ],
          },
        ],
      },
      {
        title: 'Tu contenido',
        subs: [
          {
            title: 'Tu contenido sigue siendo tuyo',
            blocks: ['Eres el titular de tu Contenido del cliente. No reclamamos su propiedad.'],
          },
          {
            title: 'La licencia que nos das',
            blocks: [
              'Nos concedes una licencia mundial, no exclusiva y libre de regalías, que solo podemos sublicenciar a los proveedores que nos ayudan a operar la Plataforma, para alojar, almacenar, copiar, procesar, transmitir, adaptar (por ejemplo, para cambiar el tamaño de imágenes, crear miniaturas y capturas para tu panel o dar formato al código), mostrar y publicar tu Contenido del cliente, únicamente para prestar, proteger, dar soporte y mejorar la Plataforma y para publicar tus Sitios como indiques.',
              'La licencia continúa para las copias de seguridad hasta que se sobrescriben en sus ciclos habituales y mientras necesitemos conservar información para cumplir una obligación legal. No usamos tu Contenido del cliente para promocionar véltica y no entrenamos modelos de inteligencia artificial con él.',
            ],
          },
          {
            title: 'Mostrar tu Sitio',
            blocks: [
              'No mostraremos públicamente tu Sitio, tu marca ni tu Contenido del cliente en nuestro marketing, galerías o escaparates sin tu permiso. Los Sitios de planes que incluyen la insignia de véltica la muestran, como se explica en el apartado 6.5.',
            ],
          },
          {
            title: 'Haz tus propias copias',
            blocks: [
              'Ofrecemos copias de seguridad según tu plan y cuidamos razonablemente tus datos, pero la Plataforma no es un archivo garantizado. Conserva tus propias copias de lo importante. Nuestra responsabilidad por la pérdida de datos está sujeta al apartado 12.',
            ],
          },
          {
            title: 'Retirar contenido',
            blocks: [
              'Puedes retirar Contenido del cliente o eliminar un proyecto en cualquier momento. Retirar un Sitio lo despublica de inmediato, y un proyecto eliminado se puede restaurar durante 30 días. Las copias que conservan cachés, buscadores y otros terceros escapan a nuestro control y pueden persistir algún tiempo.',
            ],
          },
          {
            title: 'Información sensible que no debes recoger',
            blocks: [
              'No uses formularios, reservas ni ninguna otra parte de la Plataforma para recoger números de tarjeta de pago, códigos de seguridad, credenciales bancarias, contraseñas, números de identificación oficial ni información de salud protegida, salvo que lo hayamos acordado por escrito. Los pagos con tarjeta deben pasar por Stripe; véltica no recibe ni guarda datos de tarjeta de tus Usuarios finales. véltica no está diseñada para información regulada por HIPAA y no firmamos acuerdos de asociado comercial.',
            ],
          },
          {
            title: 'Retirada por nuestra parte y protección de menores',
            blocks: [
              'Podemos retirar o desactivar Contenido del cliente que razonablemente creamos que incumple el Acuerdo o la ley. Tenemos tolerancia cero con el material de abuso sexual infantil. Si lo detectamos, lo retiraremos, conservaremos las pruebas y lo denunciaremos ante el Centro Nacional para Niños Desaparecidos y Explotados o las autoridades competentes, como exija la ley. Podemos usar herramientas automáticas para detectarlo.',
            ],
          },
        ],
      },
      {
        title: 'Tus responsabilidades',
        subs: [
          {
            title: 'Derechos sobre tu contenido',
            blocks: [
              'Debes ser titular, o tener todas las licencias y permisos necesarios, de todo lo que pongas en un Sitio, incluidos textos, imágenes, fuentes, música, vídeo, código, marcas y datos personales. Las imágenes de archivo y otros medios de terceros que añadas siguen sujetos a sus propias licencias.',
            ],
          },
          {
            title: 'Cumplimiento de la ley',
            blocks: [
              'Debes cumplir todas las leyes que apliquen a tus Sitios y a tu negocio, incluidas las de protección del consumidor, publicidad, privacidad y protección de datos, cookies y consentimiento, comunicaciones comerciales y por correo, accesibilidad, impuestos, licencias y exportación. Eres el único responsable de la política de privacidad, los términos y los avisos de tus Sitios. Las plantillas de políticas que ofrecemos son solo una comodidad: no son asesoría legal y no hacen que tu Sitio cumpla por sí solas.',
            ],
          },
          {
            title: 'Política de uso aceptable',
            blocks: ['Debes cumplir la [Política de uso aceptable](doc:aup), que forma parte del Acuerdo.'],
          },
          {
            title: 'Tus Usuarios finales',
            blocks: [
              'Eres el único responsable de tu relación con tus Usuarios finales. Tú decides qué datos personales recogen tus Sitios y para qué, haces los avisos que exige la ley y atiendes sus solicitudes. Respecto de los datos de los Usuarios finales, tú eres el responsable del tratamiento (o «negocio») y nosotros actuamos como tu encargado (o «proveedor de servicios») según el [Acuerdo de procesamiento de datos](doc:dpa). véltica no es parte de tus tratos con los Usuarios finales y no responde del contenido, los productos o los servicios de tus Sitios.',
            ],
          },
          {
            title: 'Uso de la Plataforma',
            blocks: [
              'No puedes, ni puedes ayudar a nadie a:',
              {
                list: [
                  'copiar, modificar, aplicar ingeniería inversa o descompilar la Plataforma, salvo donde la ley lo permita pese a esta restricción;',
                  'extraer datos masivamente de la Plataforma o usar bots o automatización para crear cuentas, generar contenido a escala o saltarte límites;',
                  'revender o sublicenciar la Plataforma, salvo construir y administrar Sitios para tus clientes desde tu propia Cuenta;',
                  'sondear, escanear o probar la vulnerabilidad de la Plataforma, ni interferir con ella o con otros usuarios, sin nuestro permiso por escrito;',
                  'eludir los límites del plan, la seguridad o las medidas técnicas;',
                  'falsear tu identidad o tu relación con véltica; ni',
                  'usar la Plataforma para construir un servicio competidor copiándola.',
                ],
              },
            ],
          },
          {
            title: 'Uso razonable de los recursos',
            blocks: [
              'Nuestros planes no ponen tope al número de visitas de tus Sitios. A cambio, la Plataforma está pensada para alojar y publicar sitios web, no para distribuir archivos a escala, servir de repositorio de descargas, alojar vídeo o audio para reproducirlos desde otros sitios, guardar copias o archivos ajenos, ni actuar como red de entrega de contenido para terceros. Si el consumo de una Cuenta es desproporcionado respecto de su plan, nos pondremos en contacto contigo para buscar una solución antes de limitar el servicio.',
            ],
          },
          {
            title: 'Avisar de problemas de seguridad',
            blocks: [
              'Si encuentras una vulnerabilidad, avísanos en {email} y danos un plazo razonable para corregirla antes de divulgarla. No accedas a datos que no sean tuyos, no degrades la Plataforma ni expongas a Usuarios finales mientras investigas.',
            ],
          },
        ],
      },
      {
        title: 'Servicios de terceros y términos específicos',
        subs: [
          {
            title: 'Términos específicos por producto',
            blocks: [
              'Algunas funciones tienen sus propios términos, que forman parte del Acuerdo cuando las usas: los [Términos de ventas y pagos](doc:sellers) para vender y cobrar, los [Términos de dominios](doc:domains) para dominios, los [Términos de inteligencia artificial](doc:ai) para las funciones de IA y los [Términos del programa Beta](doc:beta) para las funciones en beta.',
            ],
          },
          {
            title: 'Servicios e integraciones de terceros',
            blocks: [
              'Partes de la Plataforma dependen de terceros, por ejemplo Stripe para pagos, Google para el acceso, los calendarios y la IA, nuestro registrador de dominios, proveedores de envío de correo y las integraciones opcionales que actives. Sus términos y prácticas de privacidad aplican a tu uso de ellos, y puede que debas aceptarlos por separado. No controlamos esos servicios, no garantizamos su disponibilidad y pueden cambiar o detenerse en cualquier momento. Al conectar uno, nos autorizas a intercambiar la información necesaria para que funcione.',
            ],
          },
          {
            title: 'Contenido de terceros, enlaces y código personalizado',
            blocks: [
              'Los Sitios los crean nuestros usuarios. No los avalamos, verificamos ni controlamos y no respondemos de su contenido ni de lo que venden. Los Sitios pueden enlazar a sitios de terceros que no controlamos. Si añades código personalizado o incrustas scripts de terceros en tu Sitio, ese código se ejecuta en tu Sitio bajo tu responsabilidad, incluidas su seguridad, su comportamiento y los consentimientos que requiera.',
            ],
          },
          {
            title: 'Plantillas y medios de archivo',
            blocks: [
              'Las plantillas, bloques y diseños que ofrecemos se licencian solo para su uso en Sitios creados en la Plataforma. No puedes redistribuirlos ni revenderlos como plantillas. Las imágenes de archivo y otros medios obtenidos a través de la Plataforma están sujetos a la licencia de su origen.',
            ],
          },
        ],
      },
      {
        title: 'Lo que es nuestro',
        subs: [
          {
            title: 'Propiedad intelectual de véltica',
            blocks: [
              'véltica es titular de la Plataforma y de todo lo que la compone, incluidos su software, diseños, bibliotecas de plantillas y bloques, documentación, nombre, logotipos y cualquier mejora, salvo tu Contenido del cliente. La Plataforma se te licencia, no se te vende.',
            ],
          },
          {
            title: 'Licencia de uso de la Plataforma',
            blocks: [
              'Mientras cumplas el Acuerdo, te concedemos una licencia limitada, revocable, no exclusiva, intransferible y no sublicenciable para usar la Plataforma con su finalidad prevista.',
            ],
          },
          {
            title: 'Comentarios',
            blocks: [
              'Si nos das ideas, sugerencias o comentarios, podemos usarlos libremente, sin obligación ni compensación hacia ti y sin tener que citarte.',
            ],
          },
          {
            title: 'Funciones en beta',
            blocks: [
              'Las funciones que marcamos como beta, vista previa o «próximamente» se ofrecen conforme a los [Términos del programa Beta](doc:beta).',
            ],
          },
          {
            title: 'Nuestro nombre y la insignia de véltica',
            blocks: [
              'No uses el nombre, el logotipo ni las marcas de véltica salvo para referirte con veracidad a la Plataforma. Los Sitios de planes que incluyen la insignia de véltica deben mantenerla; no la ocultes, tapes ni elimines por medios técnicos salvo que tu plan permita quitarla.',
            ],
          },
        ],
      },
      {
        title: 'Nuestros derechos',
        subs: [
          {
            title: 'Lo importante que podemos hacer',
            blocks: [
              'Nos reservamos los siguientes derechos, que podemos ejercer en cualquier momento a nuestra discreción y, donde la ley lo permita, sin responsabilidad hacia ti: (a) cambiar cualquier parte de la Plataforma o de su funcionalidad; (b) suspender o dejar de ofrecer cualquier parte de la Plataforma; (c) limitar, suspender o terminar tu acceso a cualquier parte de la Plataforma; (d) limitar, desactivar o retirar el acceso a tu Cuenta, tus Sitios o tu Contenido del cliente; (e) cambiar nuestros criterios de elegibilidad; (f) vigilar o escanear tu Cuenta y tu Contenido del cliente en busca de abuso, spam, fraude, malware u otras infracciones, con herramientas automáticas cuando proceda, y actuar según lo que encontremos; y (g) ajustar planes, límites y precios como se describe en el apartado 15.',
            ],
          },
          {
            title: 'Cómo solemos tratar las infracciones',
            blocks: [
              'Cuando sea razonable y seguro, te pediremos que corrijas un problema antes de actuar. Las infracciones graves o repetidas, los riesgos para las personas o la Plataforma y las obligaciones legales pueden llevarnos a actuar de inmediato, incluso suspendiendo o cerrando tu Cuenta. Puedes escribir a {email} para apelar; no estamos obligados a restituir contenido ni cuentas.',
            ],
          },
          {
            title: 'Requerimientos legales',
            blocks: [
              'Podemos divulgar información cuando creamos que la ley o un proceso legal válido lo exige, o para proteger los derechos, la seguridad o los bienes de las personas o de la Plataforma. Cuando la ley lo permita y sea razonable, te lo diremos.',
            ],
          },
        ],
      },
      {
        title: 'Facturación, pagos y renovaciones',
        subs: [
          {
            title: 'Tarifas y pago',
            blocks: [
              'Los planes de pago se cobran por adelantado, cada mes o cada año, a través de nuestro procesador de pagos, Stripe. Nos autorizas a cobrar tu método de pago por el plan que elijas y por cualquier otra cosa que nos compres, como un dominio. Las Tarifas se expresan en dólares estadounidenses salvo que la Plataforma muestre otra moneda al pagar.',
            ],
          },
          {
            title: 'Renovación y cancelación',
            blocks: [
              'Los planes de pago se renuevan automáticamente por el mismo periodo hasta que cancelas. Puedes cancelar en cualquier momento desde Facturación; cancelar detiene la siguiente renovación, conservas tu plan hasta el final del periodo ya pagado y después tu Cuenta pasa al plan gratuito. Al terminar un plan no se borra nada, pero puede que no puedas añadir más de lo que permite el plan gratuito.',
            ],
          },
          {
            title: 'Reembolsos',
            blocks: [
              'Las Tarifas ya cobradas no se reembolsan, ni tampoco la parte no usada de un periodo, salvo que la ley lo exija, que no hayamos podido prestar el servicio por un fallo nuestro o que se te cobrara por error y nos escribas dentro de los 30 días siguientes al cargo. Consulta la [Política de facturación y reembolsos](doc:billing).',
            ],
          },
          {
            title: 'Impuestos',
            blocks: [
              'Las Tarifas no incluyen impuestos, aranceles ni cargos similares, de los que eres responsable salvo que al pagar te digamos que los cobramos nosotros. Si debemos cobrar un impuesto, lo añadiremos al cargo.',
            ],
          },
          {
            title: 'Pagos fallidos',
            blocks: [
              'Si un pago falla, nosotros o nuestro procesador podemos reintentarlo. Si no se puede cobrar, tus funciones de pago pueden suspenderse y tu Cuenta puede volver al plan gratuito al final del periodo, sin borrar tu contenido. Disputar un cargo con tu banco en lugar de contactarnos primero puede llevarnos a suspender la Cuenta y a bloquear la transferencia de cualquier dominio que hayamos concedido con el plan.',
            ],
          },
          {
            title: 'Ventas hechas desde tus Sitios y otros cargos',
            blocks: [
              'Las comisiones que retenemos sobre las ventas que haces desde tus Sitios están en los [Términos de ventas y pagos](doc:sellers). Los cargos de dominios se rigen por los [Términos de dominios](doc:domains).',
            ],
          },
        ],
      },
      {
        title: 'Privacidad y seguridad',
        blocks: [
          'Nuestra [Política de privacidad](doc:privacy) explica cómo tratamos los datos personales sobre ti. Para los datos personales de tus Usuarios finales que tratamos por encargo tuyo aplica el [Acuerdo de procesamiento de datos](doc:dpa), y nuestros proveedores se enumeran en la página de [Subprocesadores](doc:subprocessors).',
          'Aplicamos medidas técnicas y organizativas razonables para proteger la Plataforma y tus datos, pero ningún sistema es perfectamente seguro y no podemos garantizar una seguridad absoluta. Si detectamos una brecha de seguridad que afecte a tus datos personales, te lo diremos sin demora indebida, como exige la ley. Eres responsable de cuidar tus credenciales y de la seguridad del código y las integraciones que añadas a tus Sitios.',
        ],
      },
      {
        title: 'Derechos de autor y propiedad intelectual',
        blocks: [
          'Respetamos la propiedad intelectual y esperamos que tú también. Si crees que el contenido de un Sitio infringe tus derechos de autor, sigue el proceso de nuestra [Política de derechos de autor](doc:copyright). Podemos retirar contenido y cerrar las cuentas de quienes infringen de forma repetida.',
        ],
      },
      {
        title: 'Confidencialidad',
        subs: [
          {
            title: 'Información confidencial',
            blocks: [
              'La «Información confidencial» es la información no pública que una parte revela a la otra en relación con el Acuerdo y que está marcada como confidencial o que una persona razonable entendería como tal, como funciones sin lanzar, detalles de seguridad y precios no públicos. No incluye la información que es o pasa a ser pública sin culpa del receptor, que este ya conocía o recibió legítimamente de otro, o que desarrolla de forma independiente. El Contenido del cliente que publicas en un Sitio público no es confidencial.',
            ],
          },
          {
            title: 'Obligaciones',
            blocks: [
              'El receptor usará la Información confidencial solo para ejercer sus derechos y cumplir sus obligaciones bajo el Acuerdo, la protegerá con al menos un cuidado razonable y no la revelará salvo a quienes necesiten conocerla y estén obligados a mantenerla confidencial, o cuando la ley lo exija, en cuyo caso avisará a la otra parte cuando sea lícito. Estas obligaciones continúan durante tres años después de terminar el Acuerdo.',
            ],
          },
        ],
      },
      {
        title: 'Exclusión de garantías y limitación de responsabilidad',
        subs: [
          {
            title: 'Exclusión de garantías',
            blocks: [
              'LA PLATAFORMA SE OFRECE «TAL CUAL» Y «SEGÚN DISPONIBILIDAD». EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, VÉLTICA EXCLUYE TODA GARANTÍA, EXPRESA, IMPLÍCITA O LEGAL, INCLUIDAS LAS DE COMERCIABILIDAD, IDONEIDAD PARA UN FIN CONCRETO, TITULARIDAD Y NO INFRACCIÓN. NO GARANTIZAMOS QUE LA PLATAFORMA SEA ININTERRUMPIDA, LIBRE DE ERRORES O SEGURA, NI QUE SE CORRIJAN LOS DEFECTOS. NO PROMETEMOS NINGÚN RESULTADO, INCLUIDOS TRÁFICO, POSICIONAMIENTO EN BUSCADORES, VENTAS O INGRESOS. LOS SERVICIOS DE TERCEROS, LO GENERADO CON INTELIGENCIA ARTIFICIAL Y LAS FUNCIONES EN BETA SE OFRECEN SIN GARANTÍA DE NINGÚN TIPO. NADA DE LO QUE OFRECEMOS ES ASESORÍA LEGAL, FISCAL O FINANCIERA.',
            ],
          },
          {
            title: 'Limitación de responsabilidad',
            blocks: [
              'EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, VÉLTICA Y {operator}, Y NUESTROS CONTRATISTAS Y PROVEEDORES, NO RESPONDERÁN POR DAÑOS INDIRECTOS, INCIDENTALES, ESPECIALES, CONSECUENTES, EJEMPLARES O PUNITIVOS, NI POR PÉRDIDA DE BENEFICIOS, INGRESOS, DATOS, REPUTACIÓN U OPORTUNIDADES DE NEGOCIO, DERIVADOS DEL ACUERDO O DE LA PLATAFORMA, YA SEA POR CONTRATO, RESPONSABILIDAD EXTRACONTRACTUAL (INCLUIDA LA NEGLIGENCIA) O CUALQUIER OTRA CAUSA, AUNQUE HAYAMOS SIDO ADVERTIDOS DE LA POSIBILIDAD DE ESOS DAÑOS.',
              'EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, NUESTRA RESPONSABILIDAD TOTAL POR TODAS LAS RECLAMACIONES DERIVADAS DEL ACUERDO O DE LA PLATAFORMA NO SUPERARÁ EL MAYOR ENTRE (A) LO QUE NOS PAGASTE EN LOS DOCE MESES ANTERIORES AL HECHO QUE ORIGINE LA RECLAMACIÓN Y (B) 100 USD.',
              'En algunos lugares no se permiten ciertas exclusiones o límites, así que partes de este apartado pueden no aplicarte. Nada en el Acuerdo limita la responsabilidad que la ley no permite limitar, incluida la derivada de fraude, negligencia grave o dolo. Estos límites son una parte básica del acuerdo entre nosotros y aplican aunque un remedio no cumpla su finalidad esencial.',
            ],
          },
        ],
      },
      {
        title: 'Indemnización',
        blocks: [
          'Defenderás, indemnizarás y mantendrás indemnes a véltica, a {operator} y a nuestros contratistas y proveedores frente a cualquier reclamación, pérdida, responsabilidad, daño y coste, incluidos honorarios razonables de abogados, derivados de o relacionados con: (a) tu Contenido del cliente y tus Sitios; (b) los productos y servicios que ofreces y tus ventas, reembolsos, disputas, impuestos y tratos con Usuarios finales; (c) tu incumplimiento del Acuerdo o de cualquier ley; o (d) tu infracción o violación de derechos de terceros.',
          'Te avisaremos de la reclamación con prontitud y cooperaremos de forma razonable a tu costa. No puedes llegar a un acuerdo que nos imponga obligaciones o reconozca culpa en nuestro nombre sin nuestro consentimiento por escrito.',
        ],
      },
      {
        title: 'Cambios en la Plataforma y terminación',
        subs: [
          {
            title: 'Cambios en la Plataforma',
            blocks: [
              'La Plataforma evoluciona. Podemos añadir, cambiar o retirar funciones. Intentaremos avisar de los cambios que reduzcan de forma importante lo que incluye un plan de pago, cuando sea razonablemente posible.',
            ],
          },
          {
            title: 'Terminación por tu parte',
            blocks: [
              'Puedes dejar de usar la Plataforma y eliminar tu Cuenta en cualquier momento. Cancelar un plan de pago es aparte y se explica en el apartado 8.',
            ],
          },
          {
            title: 'Suspensión o terminación por nuestra parte',
            blocks: [
              'Podemos suspender o terminar tu acceso si incumples el Acuerdo, si tu uso supone un riesgo de seguridad o legal, si la ley nos obliga o si quedan Tarifas sin pagar. También podemos terminar el Acuerdo sin causa avisándote con 30 días de antelación; en ese caso reembolsaremos las Tarifas prepagadas correspondientes al periodo posterior a la fecha en que surta efecto. No reembolsamos las Tarifas cuando suspendemos o terminamos por un incumplimiento tuyo.',
            ],
          },
          {
            title: 'Efectos de la terminación',
            blocks: [
              'Cuando termina tu acceso, tus Sitios se despublican y cesa tu derecho a usar la Plataforma. Tratamos tus datos conforme a los plazos de conservación de la [Política de privacidad](doc:privacy), incluido el periodo en que un proyecto eliminado se puede restaurar, salvo que hayamos terminado por una infracción grave o la ley disponga otra cosa. Guarda lo que necesites antes de irte. Las Tarifas ya devengadas siguen siendo exigibles.',
            ],
          },
          {
            title: 'Lo que sobrevive',
            blocks: [
              'Sobreviven a la terminación los apartados que por su naturaleza deban continuar, incluidos los de licencias del Contenido del cliente necesarias para las copias de seguridad, titularidad, Tarifas adeudadas, confidencialidad, exclusión de garantías, limitación de responsabilidad, indemnización, disputas y las disposiciones generales.',
            ],
          },
        ],
      },
      {
        title: 'Cambios en estos Términos',
        blocks: [
          'Podemos modificar el Acuerdo de vez en cuando. Para los cambios importantes te avisaremos por correo o dentro de la Plataforma al menos 30 días antes de que surtan efecto, salvo que haga falta un plazo menor por razones legales o de seguridad o porque añadamos una función nueva. La fecha y la versión de arriba indican qué está en vigor. Si sigues usando la Plataforma cuando un cambio surte efecto, lo aceptas. Si no estás de acuerdo, cancela antes de que surta efecto. Puedes pedirnos versiones anteriores en {email}.',
          'Los precios promocionales, como los del programa Beta, se respetan durante el periodo indicado cuando los compraste.',
        ],
      },
      {
        title: 'Resolución de disputas',
        subs: [
          {
            title: 'Primero, negociación informal',
            blocks: [
              'Antes de iniciar cualquier procedimiento formal, aceptas escribirnos a {email} describiendo la disputa y lo que quieres, y ambos intentaremos de buena fe resolverla durante al menos 30 días.',
            ],
          },
          {
            title: 'Ley aplicable',
            blocks: [
              'El Acuerdo y cualquier disputa derivada de él se rigen por las leyes del Estado de California y la ley federal de Estados Unidos que aplique, sin tener en cuenta sus normas sobre conflicto de leyes. No aplica la Convención de las Naciones Unidas sobre los Contratos de Compraventa Internacional de Mercaderías.',
            ],
          },
          {
            title: 'Tribunales',
            blocks: [
              'Si una disputa no se resuelve de manera informal, se presentará exclusivamente ante los tribunales estatales o federales ubicados en el condado de {county}, California, y tú y nosotros aceptamos su jurisdicción personal y su sede.',
            ],
          },
          {
            title: 'Excepciones',
            blocks: [
              'Cualquiera de las partes puede pedir una medida cautelar u otro remedio urgente ante cualquier tribunal competente para proteger la propiedad intelectual o la información confidencial, y cualquiera puede presentar una reclamación individual ante un tribunal de reclamaciones menores cuando proceda.',
            ],
          },
          {
            title: 'Plazo',
            blocks: [
              'En la medida en que la ley lo permita, cualquier reclamación derivada del Acuerdo debe presentarse dentro de un año desde que surgió, o queda extinguida de forma definitiva.',
            ],
          },
          {
            title: 'Consumidores fuera de Estados Unidos',
            blocks: [
              'Si usas la Plataforma como consumidor, las leyes imperativas de protección del consumidor del país donde vives siguen aplicándote y, cuando la ley lo exija, puedes presentar reclamaciones ante los tribunales de tu lugar de residencia.',
            ],
          },
          {
            title: 'Aviso para usuarios de California',
            blocks: [
              'Conforme a la sección 1789.3 del Código Civil de California, los usuarios de California tienen derecho al siguiente aviso: la Plataforma la presta {operator}; los cargos se describen en el apartado 8; para resolver una queja u obtener más información, escribe a {email}; y puedes contactar a la Unidad de Asistencia de Quejas de la División de Servicios al Consumidor del Departamento de Asuntos del Consumidor de California por escrito en 1625 North Market Blvd., Suite N 112, Sacramento, California 95834, o por teléfono al (916) 445-1254 o al (800) 952-5210.',
            ],
          },
        ],
      },
      {
        title: 'Sanciones económicas y controles de exportación',
        blocks: [
          'Confirmas que no estás ubicado ni resides habitualmente en un país o territorio sujeto a sanciones integrales de Estados Unidos, que no figuras en ninguna lista estadounidense de partes restringidas o prohibidas y que no usarás la Plataforma en violación de los controles de exportación o las sanciones de Estados Unidos ni de otra jurisdicción aplicable. Podemos restringir o terminar el acceso para cumplir esas leyes.',
        ],
      },
      {
        title: 'Disposiciones generales',
        subs: [
          {
            title: 'Cesión',
            blocks: [
              'No puedes ceder ni transferir el Acuerdo, ni ningún derecho derivado de él, sin nuestro consentimiento por escrito. Nosotros podemos cederlo en relación con una fusión, adquisición, reorganización o venta de activos, o a una filial. Toda cesión que incumpla esta regla es nula.',
            ],
          },
          {
            title: 'Acuerdo íntegro y divisibilidad',
            blocks: [
              'El Acuerdo es el acuerdo íntegro entre tú y nosotros sobre la Plataforma y sustituye a los anteriores sobre el mismo tema. Si una disposición resulta inaplicable, se aplicará en la máxima medida posible y el resto del Acuerdo seguirá en vigor.',
            ],
          },
          {
            title: 'Sin renuncia',
            blocks: ['Si en algún momento no hacemos valer un derecho, no lo abandonamos. Una renuncia solo es válida si es por escrito y está firmada por nosotros.'],
          },
          {
            title: 'Hechos fuera de nuestro control',
            blocks: [
              'No respondemos por un fallo o retraso causado por hechos fuera de nuestro control razonable, incluidas caídas de internet, de proveedores de alojamiento o de pagos, ataques, desastres naturales, guerra, actos de gobierno, conflictos laborales o pandemias.',
            ],
          },
          {
            title: 'Avisos y comunicaciones electrónicas',
            blocks: [
              'Aceptas recibir nuestros avisos por medios electrónicos, por correo a la dirección de tu cuenta o dentro de la Plataforma, y que cumplen cualquier requisito legal de que un aviso conste por escrito. Los avisos para nosotros van a {email}.',
            ],
          },
          {
            title: 'Sin terceros beneficiarios; relación entre las partes',
            blocks: [
              'El Acuerdo no crea terceros beneficiarios, y nada en él crea una sociedad, empresa conjunta, relación laboral ni de agencia entre tú y nosotros.',
            ],
          },
          {
            title: 'Interpretación',
            blocks: ['«Incluido» significa «incluido, sin limitación». Los títulos son solo por comodidad y no afectan a la interpretación.'],
          },
          {
            title: 'Idiomas',
            blocks: [
              'Publicamos el Acuerdo en inglés y en español. La traducción es solo por comodidad; si hay discrepancia, prevalece la versión en inglés en la medida en que la ley lo permita.',
            ],
          },
          {
            title: 'Contacto',
            blocks: ['Preguntas sobre estos Términos: {email}.'],
          },
        ],
      },
    ],
  },
}
